import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Textarea } from "@/shared/components/ui/textarea";
import { Label } from "@/shared/components/ui/label";
import { useNavigate } from "react-router";
import { useSession } from "@/platform/auth";
import { templateBuilderMutations } from "@/features/template-builder/mutations";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { templateBuilderQueries } from "@/features/template-builder/queries";
import { getErrorMessage } from "@/shared/lib/error-message";

export type CreateTemplateDialogProps = {
  trigger: React.ReactNode;
};

export function CreateTemplateDialog({ trigger }: CreateTemplateDialogProps) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [descriptionValue, setDescriptionValue] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { user } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const canSubmit = !!name.trim() && !!user;

  const { mutate: createTemplate, isPending } = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Missing user");

      return await templateBuilderMutations.createTemplate({
        name: name.trim(),
        description: descriptionValue.trim(),
      });
    },
    onSuccess: (analysisId: string) => {
      setOpen(false);
      setName("");
      setDescriptionValue("");
      void queryClient.invalidateQueries(templateBuilderQueries.lists());
      void navigate(`/template-builder/${analysisId}`);
    },
    onError: (e) => {
      console.error(e);
      setError(
        getErrorMessage(e, "Failed to create template. Please try again."),
      );
    },
  });

  function handleCreate() {
    if (!canSubmit) return;
    setError(null);
    createTemplate();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        setError(null);
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create template</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="tpl-name">Name</Label>
            <Input
              id="tpl-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Competitor Landscape Template"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="tpl-description">Description</Label>
            <Textarea
              id="tpl-description"
              value={descriptionValue}
              onChange={(e) => setDescriptionValue(e.target.value)}
              placeholder="What is this template about?"
              rows={4}
            />
          </div>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={!canSubmit || isPending}>
            {isPending ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
